import { useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Bell, CheckCircle2 } from "lucide-react";
import {
  Body,
  Caption,
  VBadge,
  VButton,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuTrigger,
  cn,
} from "@/components/ui";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import {
  NOTIFICATION_KIND_META,
  notificationIconTone,
  notificationTextTone,
  relativeDays,
  useNotifications,
} from "@/v2/meldingen/notifications";

// Notificatiebel (PZAO-41): één compacte lijst met de meest urgente meldingen
// voor de ingelogde gebruiker (gericht en team gemengd, gangbaar inbox-
// patroon). De badge telt enkel de nog niet bekeken meldingen; het openen van
// het paneel markeert de getoonde meldingen als bekeken (per gebruiker). De
// volledige lijst met details staat op de meldingenpagina (/meldingen).
const PANEL_LIMIT = 5;

export function V2Notifications(): React.JSX.Element | null {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { items, total, unreadCount, markSeen } = useNotifications();

  // Snapshot van de nieuw-status bij het openen: de dots blijven zichtbaar
  // zolang het paneel open staat, ook al is alles intussen als bekeken gezet.
  const [newAtOpen, setNewAtOpen] = useState<ReadonlySet<string>>(new Set());

  // Meldingen zijn een beheerdersfunctie (net als de taken zelf).
  if (user.role !== "manager") return null;

  const hidden = total - PANEL_LIMIT;

  function handleOpenChange(open: boolean): void {
    if (!open) return;
    const visible = items.slice(0, PANEL_LIMIT);
    setNewAtOpen(new Set(visible.filter((n) => n.isUnread).map((n) => n.key)));
    // Enkel wat effectief in beeld komt telt als bekeken; wat achter
    // "+ n meer" zit, blijft nieuw tot de meldingenpagina bezocht wordt.
    markSeen(visible.map((n) => n.key));
  }

  return (
    <VDropdownMenu onOpenChange={handleOpenChange}>
      <VDropdownMenuTrigger asChild>
        <VButton
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount === 0
              ? `Meldingen${total > 0 ? `, ${total} openstaand` : ""}`
              : `Meldingen, ${unreadCount} nieuw`
          }
          className="relative text-on-primary hover:bg-primary-700 hover:text-on-primary"
        >
          <Bell className="size-5" aria-hidden />
          {unreadCount > 0 ? (
            <span
              className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-primary-800"
              aria-hidden
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          ) : null}
        </VButton>
      </VDropdownMenuTrigger>
      <VDropdownMenuContent align="end" className="w-[400px] rounded-xl p-0 shadow-lg">
        <div className="border-b border-border px-4 py-3">
          <Body weight="semibold">Meldingen</Body>
        </div>

        {total === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-success-50 text-success-600">
              <CheckCircle2 className="size-5" aria-hidden />
            </span>
            <Body weight="medium">Alles opgevolgd</Body>
            <Caption tone="muted" as="p">
              Meldingen verschijnen zodra iets jouw aandacht vraagt.
            </Caption>
          </div>
        ) : (
          <div className="py-1.5">
            <ul>
              {items.slice(0, PANEL_LIMIT).map((n) => {
                const meta = NOTIFICATION_KIND_META[n.kind];
                const isNew = newAtOpen.has(n.key);
                const isReminder = n.kind === "taskReminder";
                return (
                  <VDropdownMenuItem
                    key={n.key}
                    className="mx-1.5 items-start gap-3 rounded-lg px-2.5 py-2.5"
                    onSelect={() => navigate(n.to)}
                  >
                    {/* Visueel onderscheid (PZAO-41): taakherinnering = vierkante
                        tegel, vervalsignaal = ronde tegel. */}
                    <span
                      className={cn(
                        "mt-0.5 flex size-7 shrink-0 items-center justify-center",
                        isReminder ? "rounded-lg" : "rounded-full",
                        n.taskDone
                          ? "bg-success-50 text-success-600"
                          : notificationIconTone(n.days),
                      )}
                    >
                      <meta.icon className="size-3.5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <Body
                        weight={isNew ? "semibold" : "medium"}
                        className="truncate leading-snug"
                      >
                        {n.title}
                      </Body>
                      <Caption tone="muted" as="p" className="truncate">
                        {n.context}
                      </Caption>
                    </span>
                    <span className="mt-0.5 flex shrink-0 items-center gap-2">
                      {isReminder ? (
                        <VBadge color={n.taskDone ? "success" : "neutral"} size="count">
                          {n.taskDone ? "Afgewerkt" : "Open"}
                        </VBadge>
                      ) : null}
                      <Caption
                        className={n.taskDone ? "text-success-700" : notificationTextTone(n.days)}
                      >
                        {relativeDays(n.days)}
                      </Caption>
                      <span
                        className={cn(
                          "size-2 rounded-full",
                          isNew ? "bg-info-500" : "bg-transparent",
                        )}
                        aria-label={isNew ? "Nieuw" : undefined}
                      />
                    </span>
                  </VDropdownMenuItem>
                );
              })}
            </ul>
            {hidden > 0 ? (
              <Caption tone="muted" as="p" className="px-4 pb-1 pt-0.5">
                + {hidden} meer
              </Caption>
            ) : null}
          </div>
        )}

        <div className="border-t border-border p-1.5">
          <VDropdownMenuItem
            onSelect={() => navigate("/meldingen")}
            className="justify-center gap-1.5 rounded-lg py-2 font-medium text-primary-700 focus:text-primary-700"
          >
            Alle meldingen bekijken
            <ArrowRight className="size-3.5" aria-hidden />
          </VDropdownMenuItem>
        </div>
      </VDropdownMenuContent>
    </VDropdownMenu>
  );
}
